import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sparkles, User } from 'lucide-react';
import SectionHeader from '@/components/shared/SectionHeader';
import RichContent from '@/components/shared/RichContent';
import { stripHtml } from '@/lib/richContent';
import { useSite } from '@/context/SiteContext';

const AboutSection = () => {
    const { settings } = useSite();
    if (!settings) return null;
    return (
        <section className="section-pad" data-testid="home-about-section">
            <div className="container-page grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-7">
                    <SectionHeader
                        eyebrow="About VVVSI"
                        title="A trust for jobs, learning, health & community."
                        description={stripHtml(settings.about_short)}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        viewport={{ once: true, margin: '-60px' }}
                        className="mt-6 text-foreground/80 leading-relaxed text-[15px]"
                    >
                        <RichContent html={settings.about_long} />
                    </motion.div>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {(settings.compliance_badges || []).map((b) => (
                            <Badge
                                key={b}
                                variant="secondary"
                                className="bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] font-semibold"
                            >
                                {b} Certified
                            </Badge>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true, margin: '-60px' }}
                    className="lg:col-span-5"
                >
                    <Card className="border-border shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-4">
                                <Sparkles className="h-4 w-4" />
                                <span className="text-xs font-semibold uppercase tracking-widest">
                                    Leadership
                                </span>
                            </div>
                            <div className="space-y-5">
                                <div className="flex items-center gap-4">
                                    <div className="h-14 w-14 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                                        {settings.chairman_photo ? (
                                            <img
                                                src={settings.chairman_photo}
                                                alt={settings.chairman_name}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <User className="h-6 w-6 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-foreground">
                                            {settings.chairman_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {settings.chairman_title}
                                        </div>
                                    </div>
                                </div>
                                <Separator />
                                <div className="flex items-center gap-4">
                                    <div className="h-14 w-14 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden">
                                        {settings.managing_trustee_photo ? (
                                            <img
                                                src={settings.managing_trustee_photo}
                                                alt={settings.managing_trustee_name}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <User className="h-6 w-6 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-foreground">
                                            {settings.managing_trustee_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {settings.managing_trustee_title}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Separator className="my-5" />
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="text-muted-foreground">Website</div>
                                    <div className="font-medium text-foreground">
                                        {settings.website}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">Phone</div>
                                    <div className="font-medium text-foreground">
                                        {settings.contact_phone}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </section>
    );
};

export default AboutSection;
